import { useId, useRef } from 'react'
import { MAX_SAMPLE_COPIES, MAX_SAMPLE_SUMMARY, SAMPLE_DEFAULTS, SAMPLE_LEG_COUNTS, SAMPLE_PLAYER_NUMBERS, TEMPLATE_SAMPLE_PACK, sampleIsDefault, typedPlayerNumber } from './prepackCardModel'
import { TeamSelect } from './TemplateTeamSelect'
import { FIELD, SEGMENT, segmentButton } from './templateBuilderStyles'

const NON_DIGITS_PATTERN = /\D/g
const LEADING_ZEROES_PATTERN = /^0+(?=\d)/

// The pack title limit the server validates (Prepack.Validate in apps/shared/pkg/prepacks/rules.go): any title a pack can have can be tried.
const MAX_PACK_TITLE = 255
const digitsOnly = (value, length) => value.replace(NON_DIGITS_PATTERN, '').slice(0, length)
const SAMPLE_LABEL = 'mb-1.5 block text-[var(--prepacks-text)]'
// The team the sample shows when neither the template nor the operator picked one: the server's default.
const SAMPLE_TEAM_NAME = TEMPLATE_SAMPLE_PACK.legs.find((leg) => leg.team)?.team.name
// 16px text on a phone: iOS zooms into a focused field set smaller.
const SAMPLE_FIELD = `${FIELD} h-8 text-base sm:text-xs placeholder:text-[var(--prepacks-muted)]`
const SAMPLE_TEXT_BUTTON = 'min-h-6 shrink-0 text-[var(--prepacks-accent-text)] underline-offset-2 hover:underline'

/**
 * What the preview's sample pack says (#602): its title, summary, team, legs, bets placed and, while the template
 * shows them, the player legs' shirt numbers (`playerNames` names their fields). Demo only: it never changes the
 * card or its colors, and it is saved with the template so it reopens as it was left (#997). `onChange` is the
 * sample's state setter.
 */
export function SampleContentPanel({ sample, defaultSummary, templateTeam, teamsQuery, previewTeam, numbersShown, playerNames, onChange }) {
  const fieldId = useId()
  const titleRef = useRef(null)
  const set = (key, value) => onChange((current) => ({ ...current, [key]: value }))
  // Picking the template's own team is the same as following it, so a later change of template team still shows.
  const pickTeam = (option) => set('team', !option || option.value === templateTeam ? '' : option.value)
  const setNumber = (index, value) => onChange((current) => ({ ...current, numbers: current.numbers.map((number, numberIndex) => (numberIndex === index ? typedPlayerNumber(value) : number)) }))
  // "007" is 7 bets; a lone 0 stays, as the empty-counter case.
  const setCopies = (value) => set('copies', digitsOnly(value, String(MAX_SAMPLE_COPIES).length).replace(LEADING_ZEROES_PATTERN, ''))
  const reset = () => {
    onChange(SAMPLE_DEFAULTS)
    // The button goes once the sample is back to its defaults; focus goes to the first field instead of the page.
    titleRef.current?.focus()
  }
  // The way back sits in the label's row, so the grid keeps its height whether or not the team is overridden.
  const followTemplate = sample.team ? (
    <button type="button" onClick={() => set('team', '')} className={`${SAMPLE_TEXT_BUTTON} -my-1`}>
      {templateTeam ? 'Use template’s team' : `Show the ${SAMPLE_TEAM_NAME}`}
    </button>
  ) : null
  return (
    <>
      <div className="mb-3 flex min-h-6 items-center justify-between gap-3">
        <p className="text-[var(--prepacks-muted)]">Saved with the template; only this preview uses it.</p>
        {sampleIsDefault(sample) ? null : <button type="button" onClick={reset} className={SAMPLE_TEXT_BUTTON}>Reset sample</button>}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor={`${fieldId}-title`} className={SAMPLE_LABEL}>Title</label>
          <input ref={titleRef} id={`${fieldId}-title`} type="text" name="sample-title" autoComplete="off" maxLength={MAX_PACK_TITLE} value={sample.title} placeholder={TEMPLATE_SAMPLE_PACK.title}
            onChange={(event) => set('title', event.target.value)} className={SAMPLE_FIELD} />
        </div>
        <div>
          <label htmlFor={`${fieldId}-summary`} className={SAMPLE_LABEL}>Summary</label>
          <input id={`${fieldId}-summary`} type="text" name="sample-summary" autoComplete="off" maxLength={MAX_SAMPLE_SUMMARY} value={sample.summary} placeholder={defaultSummary}
            onChange={(event) => set('summary', event.target.value)} className={SAMPLE_FIELD} />
        </div>
        <div>
          <TeamSelect label="Team" ariaLabel="Sample team" name="sample-team" value={sample.team || templateTeam} teamsQuery={teamsQuery} previewTeam={previewTeam}
            clearable={Boolean(sample.team)} placeholder={SAMPLE_TEAM_NAME} labelAction={followTemplate} onChange={pickTeam} />
        </div>
        <div>
          <span id={`${fieldId}-legs`} className={SAMPLE_LABEL}>Legs</span>
          {/* Shorter than the preview's own segments: as tall as the h-8 fields beside it. */}
          <div className={`${SEGMENT} h-8 w-fit !p-0.5`} role="group" aria-labelledby={`${fieldId}-legs`}>
            {SAMPLE_LEG_COUNTS.map((count) => (
              <button key={count} type="button" aria-pressed={count === sample.legs} aria-label={`${count} legs`} onClick={() => set('legs', count)} className={`${segmentButton(count === sample.legs)} !py-1`}>{count}</button>
            ))}
          </div>
        </div>
        <div>
          <label htmlFor={`${fieldId}-copies`} className={SAMPLE_LABEL}>Bets placed</label>
          {/* A text field: a number field reports "" for "e", "-" or ".", so what it shows and the sample would disagree. */}
          <input id={`${fieldId}-copies`} type="text" name="sample-bets-placed" autoComplete="off" inputMode="numeric" pattern="[0-9]*" value={sample.copies}
            placeholder={String(TEMPLATE_SAMPLE_PACK.copies)} onChange={(event) => setCopies(event.target.value)} className={`${SAMPLE_FIELD} !w-28 tabular-nums`} />
        </div>
        {numbersShown ? (
          <fieldset className="min-w-0">
            <legend className={SAMPLE_LABEL}>Shirt numbers</legend>
            <div className="flex gap-2">
              {SAMPLE_PLAYER_NUMBERS.map((_number, index) => (
                <input key={index} type="text" name={`sample-shirt-number-${index + 1}`} aria-label={`${playerNames[index] || `Player ${index + 1}`} shirt number`} autoComplete="off" inputMode="numeric" pattern="[0-9]*"
                  value={sample.numbers[index]} onChange={(event) => setNumber(index, event.target.value)} className={`${SAMPLE_FIELD} !w-16 text-center tabular-nums`} />
              ))}
            </div>
          </fieldset>
        ) : null}
      </div>
    </>
  )
}
